import React from 'react'
import { Review } from 'hhsoig-design-system-core'
import css from './Inspect.module.css'
import {
  formatAddress,
  formatAttachedFiles,
  formatPersonName,
  formatReviewString,
  replacePhraseWithString
} from '../../utilities'
import { dateToString } from '../../utilities/methods/dateToString'
import { Divider } from '../divider'
import { PrintReview } from '../PrintReview'
import { ReviewGrantsContracts } from '../ReviewGrantsContracts'
import { ReviewSubjects } from '../ReviewSubjects'
import { ReviewWitnesses } from '../ReviewWitnesses'
import {
  addressLabel,
  aliasLabel,
  cellPhoneLabel,
  dobLabel,
  employerLabel,
  employerPhoneLabel,
  hhsEmployeeTypeLabel,
  homePhoneLabel,
  jobTitleLabel,
  homeAddressLabel,
  medicareNumberLabel,
  medicaidNumberLabel,
  nameLabel,
  personalEmailLabel,
  ssnLabel,
  workEmailLabel,
  workPhoneLabel,
  retaliationLabel
} from '../../strings/labels'

// Return an array with Narrative and Attachments
export const complaintDescription = values => {
  return [
    {
      field_name: 'narrative',
      value: formatReviewString(values.narrative)
    },
    // Using 'evidence_description' as the field_name
    // so setting focus on an input field will work in OpDiv review.
    // Cannot set focus on 'documents' which is the actual name.
    {
      heading: 'Attached files',
      field_name: 'evidence_description',
      value: formatAttachedFiles(values.documents)
    },
    {
      heading: 'Evidence description',
      field_name: 'evidence_description',
      value: formatReviewString(values.evidence_description)
    }
  ]
}

export const Inspect = ({
  goBackToPage,
  pages,
  values,
  allegationDetails,
  childDetails
}) => {
  const contact_choices = require('../../choices/json/communication_contact.json')
  const permission_choices = require('../../choices/json/disclosure_permission.json')
  const employee_type_choices = require('../../choices/json/hhs_employee_type.json')
  const still_occurring_choices = require('../../choices/json/activity_still_occurring.json')

  const still_occurring_choice = still_occurring_choices[values.still_occurring]

  if (values.date_of_activity && values.date_of_activity.yearValue) {
    allegationDetails.push(
      {
        heading: 'Date of activity',
        field_name: 'date_of_activity',
        value: formatReviewString(
          values.date_of_activity &&
            values.date_of_activity.yearValue &&
            dateToString(values.date_of_activity, 'MDY')
        )
      },
      {
        heading: 'Is the activity still happening?',
        field_name: 'still_occurring',
        value: formatReviewString(still_occurring_choice)
      }
    )
    if (still_occurring_choice !== 'Yes') {
      allegationDetails.push({
        heading: 'Activity end date',
        field_name: 'activity_end_date',
        omitEdit: still_occurring_choice === 'Not sure',
        value:
          still_occurring_choice === 'No'
            ? formatReviewString(
                values.activity_end_date &&
                  values.activity_end_date.yearValue &&
                  dateToString(values.activity_end_date, 'MDY')
              )
            : 'Date Unknown'
      })
    }
  }

  const complaint_history = [
    {
      heading: 'Previously reported',
      field_name: 'previously_reported',
      value: formatReviewString(values.previously_reported)
    },
    {
      heading: 'Previously reported to',
      field_name: 'communication_contact',
      value: formatReviewString(
        replacePhraseWithString(
          [contact_choices[values.communication_contact]],
          values.communication_contact_other
        ).join(', ')
      )
    },
    {
      heading: 'Date previously reported',
      field_name: 'previously_reported_date',
      value: formatReviewString(
        values.previously_reported_date &&
          values.previously_reported_date.yearValue &&
          dateToString(values.previously_reported_date, 'MDY')
      )
    }
  ]

  let victim_personal_details = []
  let victim_contact_details = []
  if (values.report_on_behalf && values.report_on_behalf == 'Yes') {
    victim_personal_details.push(
      {
        heading: 'Reporting on behalf of someone else',
        field_name: 'report_on_behalf',
        value: formatReviewString(values.report_on_behalf)
      },
      {
        heading: nameLabel,
        field_name: 'victim_name',
        value: formatReviewString(
          `${formatPersonName(
            values.victim_first_name,
            values.victim_middle_name,
            values.victim_last_name,
            values.victim_suffix
          )}`
        )
      },
      {
        heading: aliasLabel,
        field_name: 'victim_alias_nickname',
        value: formatReviewString(values.victim_alias_nickname)
      },
      {
        heading: dobLabel,
        field_name: 'victim_dob',
        value: formatReviewString(
          values.victim_dob &&
            values.victim_dob.yearValue &&
            dateToString(values.victim_dob, 'MDY')
        )
      },
      {
        heading: medicareNumberLabel,
        field_name: 'victim_medicare_number',
        value: formatReviewString(values.victim_medicare_number)
      },
      {
        heading: medicaidNumberLabel,
        field_name: 'victim_medicaid_number',
        value: formatReviewString(values.victim_medicaid_number)
      },
      {
        heading: ssnLabel,
        field_name: 'victim_ssn',
        value: formatReviewString(values.victim_ssn)
      }
    )

    victim_contact_details.push(
      {
        heading: homePhoneLabel,
        field_name: 'victim_personal_phone',
        value: formatReviewString(values.victim_personal_phone)
      },
      {
        heading: cellPhoneLabel,
        field_name: 'victim_alternative_phone',
        value: formatReviewString(values.victim_alternative_phone)
      },
      {
        heading: personalEmailLabel,
        field_name: 'victim_email',
        value: formatReviewString(values.victim_email)
      },
      {
        heading: homeAddressLabel,
        field_name: 'victim_mailing_address',
        value: formatReviewString(formatAddress(values.victim_address))
      }
    )
  }

  const description_details = complaintDescription(values)

  const complainant_details = [
    {
      heading: 'Your consent to disclose',
      field_name: 'perm_to_disclose',
      value: permission_choices[values.perm_to_disclose]
    },
    {
      heading: nameLabel,
      field_name: 'complainant_first_name',
      value: formatReviewString(
        formatPersonName(
          values.complainant_first_name,
          values.complainant_middle_name,
          values.complainant_last_name,
          values.complainant_suffix
        )
      )
    },
    {
      heading: dobLabel,
      field_name: 'complainant_dob',
      value: formatReviewString(
        values.complainant_dob &&
          values.complainant_dob.yearValue &&
          dateToString(values.complainant_dob, 'MDY')
      )
    },
    {
      heading: medicareNumberLabel,
      field_name: 'complainant_medicare_number',
      value: formatReviewString(values.complainant_medicare_number)
    },
    {
      heading: medicaidNumberLabel,
      field_name: 'complainant_medicaid_number',
      value: formatReviewString(values.complainant_medicaid_number)
    },
    {
      heading: ssnLabel,
      field_name: 'complainant_ssn',
      value: formatReviewString(values.complainant_ssn)
    }
  ]

  const complainant_contact_details = [
    {
      heading: homePhoneLabel,
      field_name: 'complainant_home_phone',
      value: formatReviewString(values.complainant_home_phone)
    },
    {
      heading: cellPhoneLabel,
      field_name: 'complainant_cell_phone',
      value: formatReviewString(values.complainant_cell_phone)
    },
    {
      heading: workPhoneLabel,
      field_name: 'complainant_work_phone',
      value: formatReviewString(values.complainant_work_phone)
    },
    {
      heading: personalEmailLabel,
      field_name: 'complainant_personal_email',
      value: formatReviewString(values.complainant_personal_email)
    },
    {
      heading: workEmailLabel,
      field_name: 'complainant_work_email',
      value: formatReviewString(values.complainant_work_email)
    },
    {
      heading: 'Your home address',
      field_name: 'complainant_address',
      value: formatReviewString(formatAddress(values.complainant_address))
    }
  ]

  const employer_information = []
  if (values.complainant_hhs_employee)
    employer_information.push({
      heading: 'HHS employee, contractor, or grantee',
      field_name: 'complainant_hhs_employee',
      value: formatReviewString(values.complainant_hhs_employee)
    })

  if (values.complainant_hhs_employee === 'Yes') {
    employer_information.push(
      {
        heading: hhsEmployeeTypeLabel,
        field_name: 'employee_type',
        value: formatReviewString(
          replacePhraseWithString(
            [employee_type_choices[values.employee_type]],
            values.employee_type_other
          ).join(', ')
        )
      },
      {
        heading: jobTitleLabel,
        field_name: 'complainant_job_title',
        value: formatReviewString(values.complainant_job_title)
      },
      {
        heading: employerLabel,
        field_name: 'complainant_employer',
        value: formatReviewString(values.complainant_employer)
      },
      {
        heading: addressLabel,
        field_name: 'complainant_work_address',
        value: formatReviewString(
          formatAddress(values.complainant_work_address)
        )
      },
      {
        heading: employerPhoneLabel,
        field_name: 'complainant_employer_phone',
        value: formatReviewString(values.complainant_employer_phone)
      }
    )
  }
  if (values.retaliated_against) {
    employer_information.push({
      heading: retaliationLabel,
      field_name: 'retaliated_against',
      value: formatReviewString(values.retaliated_against)
    })
  }

  const onEditClick = field_name =>
    goBackToPage(findPageIdOfField(field_name, pages))

  const findPageIdOfField = name => {
    let page
    page = pages.find(p => p.fields.some(f => f.name === name))
    if (!page) {
      // if page not found, search conditional fields
      page = pages.find(p =>
        p.fields.some(fld => {
          let conditionalFld
          if (fld.conditionals) {
            conditionalFld = fld.conditionals.find(c =>
              c.fields.some(f => f.name === name)
            )
            return conditionalFld ? true : false
          } else {
            return false
          }
        })
      )
    }
    return page ? page.id : null
  }

  // Create and return a <Review> tag for each detail object in 'details'.
  // The detail objects are defined above, and have 3 elements:
  // heading, field_name, value
  const iterateDetails = details => {
    return details.map((detail, i) => {
      const additionalProps = detail.omitEdit
        ? {}
        : {
            onEditClick: () => {
              goBackToPage(findPageIdOfField(detail.field_name))
            },
            editHref: 'javascript:void(0)'
          }
      return (
        <Review
          className={css.reviewContent}
          key={`${detail.field_name}_${i}`}
          heading={detail.heading}
          {...additionalProps}
        >
          {detail.value}
        </Review>
      )
    })
  }

  return (
    <div data-testid="review-complaint">
      <h1 className="form-pg-title">Your complaint summary</h1>
      <p className="ds-text">
        Before you submit, make sure you check your information and save or
        print a copy for your records.
      </p>
      <h2>Allegation details</h2>
      {iterateDetails(allegationDetails)}
      {childDetails && childDetails.length > 0 && (
        <React.Fragment>
          <h3>Age of {childDetails.length > 1 ? 'children' : 'child'}</h3>
          {iterateDetails(childDetails)}
        </React.Fragment>
      )}
      {values.award_type_0 && (
        <React.Fragment>
          <h2>Grant/Contract information</h2>
          <ReviewGrantsContracts values={values} onClick={onEditClick} />
        </React.Fragment>
      )}
      {values.previously_reported == 'Yes' && (
        <React.Fragment>
          <h2>Complaint history</h2>
          {iterateDetails(complaint_history)}
        </React.Fragment>
      )}
      {values.subject_type_0 != 'unknown' && (
        <React.Fragment>
          <h2>Subject information</h2>
          <ReviewSubjects values={values} onClick={onEditClick} />
        </React.Fragment>
      )}
      {values.report_on_behalf && values.report_on_behalf == 'Yes' && (
        <React.Fragment>
          <h2>Victim information</h2>
          {iterateDetails(victim_personal_details)}
          <h3>Victim contact details</h3>
          {iterateDetails(victim_contact_details)}
        </React.Fragment>
      )}

      {values.witness_corroboration == 'Yes' && (
        <React.Fragment>
          <h2>Witness information</h2>
          <ReviewWitnesses values={values} onClick={onEditClick} />
        </React.Fragment>
      )}
      <h2>Your narrative</h2>
      <h3>Your description of events</h3>
      {iterateDetails(description_details)}
      <h2>Your information</h2>
      {values.perm_to_disclose != 'Anonymous' && (
        <React.Fragment>
          {iterateDetails(complainant_details)}
          <h3>Your contact details</h3>
          {iterateDetails(complainant_contact_details)}
          <h3>Your employment information</h3>
          {iterateDetails(employer_information)}
        </React.Fragment>
      )}
      <Divider />
      <PrintReview className={css.printReview} />
      <Divider />
    </div>
  )
}
